



import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal, effect } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from './core/auth/auth.service';
import { CommonModule } from '@angular/common';
import { ThemeService } from './core/services/theme.service';
import { Notification as AppNotification } from './core/models/loan.model';
import { ClientService } from './core/services/client.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { ToastComponent } from './shared/ui/toast.component';
import { FooterComponent } from './shared/ui/footer.component';
import { ActingContextService } from './core/services/acting-context.service';
import { FinancialTestService } from './core/services/financial-test.service';
import { AnalyticsService } from './core/services/analytics.service';
import { ConfirmationModalComponent } from './shared/ui/confirmation-modal.component';
import { ModalDiscardGuardService } from './core/services/modal-discard-guard.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, CommonModule, ToastComponent, FooterComponent, ConfirmationModalComponent]
})
export class AppComponent implements OnInit {
  authService = inject(AuthService);
  themeService = inject(ThemeService);
  router: Router = inject(Router);
  clientService = inject(ClientService);
  actingContext = inject(ActingContextService);
  private financialTestService = inject(FinancialTestService);
  private analyticsService = inject(AnalyticsService);
  modalDiscardGuard = inject(ModalDiscardGuardService);

  private routeSignal = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  constructor() {
    // Track page views
    effect(() => {
      const url = this.routeSignal();
      if (url) {
        this.analyticsService.logPageView(url as string);
      }
    });

    // Check if user is a trustee whenever profile changes
    effect(() => {
      const user = this.authService.userProfile();
      if (user && user.role === 'Client') {
        this.clientService.hasManagedClients(user.uid).then(isTrustee => {
          this.isTrustee.set(isTrustee);
        });
      } else {
        this.isTrustee.set(false);
      }
    });
  }

  isPortalRoute = computed(() => {
    const route = this.routeSignal() as string;
    return route.startsWith('/admin') || route.startsWith('/client') || route.startsWith('/borrower');
  });

  isAdminRoute = computed(() => {
    const route = this.routeSignal() as string;
    return route.startsWith('/admin');
  });

  isClientRoute = computed(() => {
    const route = this.routeSignal() as string;
    return route.startsWith('/client');
  });

  isBorrowerRoute = computed(() => {
    const route = this.routeSignal() as string;
    return route.startsWith('/borrower');
  });

  showNotifications = signal(false);
  unreadNotifications = this.clientService.unreadNotifications;
  mobileMenuOpen = signal(false);

  // Signal to track if the current user is a trustee (has managed clients)
  isTrustee = signal(false);

  // Computed nav items that include conditional Trustee Dashboard
  // Show the link only for users with Trustee or ReadOnly sub-role
  clientNavItems = computed(() => {
    const user = this.authService.userProfile();
    const baseItems = [
      { path: '/client/dashboard', label: 'Opportunities' },
      { path: '/client/portfolio', label: 'My Portfolio' },
      { path: '/client/messages', label: 'Messages' },
      { path: '/client/support', label: 'Support' },
      { path: '/client/profile', label: 'Profile' },
    ];

    // Show Trustee Dashboard link only if the user is a confirmed trustee
    if (user?.role === 'Client' && this.isTrustee()) {
      return [
        { path: '/client/trustee-dashboard', label: 'Trustee Dashboard' },
        ...baseItems
      ];
    }

    return baseItems;
  });

  borrowerNavItems = computed(() => [
    { path: '/borrower/dashboard', label: 'My Loans', exact: true },
    { path: '/borrower/messages', label: 'Messages' },
    { path: '/borrower/support', label: 'Support' },
    { path: '/borrower/profile', label: 'Profile' },
  ]);

  portalNavItems = computed(() => {
    const role = this.authService.userProfile()?.role;
    if (role === 'Borrower') {
      return this.borrowerNavItems();
    }

    return this.clientNavItems().map(item => ({
      ...item,
      exact: item.path === '/client/dashboard',
    }));
  });

  portalHomePath = computed(() => {
    const role = this.authService.userProfile()?.role;
    return role === 'Borrower' ? '/borrower/dashboard' : '/client/dashboard';
  });

  portalMessagesPath = computed(() => {
    const role = this.authService.userProfile()?.role;
    return role === 'Borrower' ? '/borrower/messages' : '/client/messages';
  });

  portalRoleLabel = computed(() => {
    const role = this.authService.userProfile()?.role;
    return role === 'Borrower' ? 'Borrower' : 'Client';
  });

  footerNavItems = computed(() => this.portalNavItems());

  ngOnInit() {
    // Run financial regression tests on app startup
    this.financialTestService.runTests();
  }

  /**
   * Clear acting context and navigate back to Trustee Dashboard
   */
  clearActingAndNavigate() {
    this.actingContext.clearActingContext();
    this.router.navigate(['/client/trustee-dashboard']);
  }

  async logout() {
    this.mobileMenuOpen.set(false);
    await this.authService.logout();
    // After logout is complete, navigate to the login page.
    this.router.navigate(['/login']);
  }

  onNotificationClick(notification: AppNotification) {
    this.clientService.markNotificationAsRead(notification.id);
    this.showNotifications.set(false);

    // Use actionUrl if available, otherwise default to deal page
    if (notification.actionUrl) {
      const actionUrl = this.authService.userProfile()?.role === 'Borrower' && notification.actionUrl.startsWith('/client/')
        ? notification.actionUrl.replace('/client/', '/borrower/')
        : notification.actionUrl;
      this.router.navigateByUrl(actionUrl);
    } else {
      const portalBase = this.authService.userProfile()?.role === 'Borrower' ? '/borrower' : '/client';
      this.router.navigate([portalBase, 'deal', notification.dealId]);
    }
  }

  markAllAsRead() {
    this.clientService.markAllNotificationsAsRead();
  }

  /**
   * Get CSS class for notification priority
   */
  getPriorityClass(priority?: string): string {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500';
      case 'MEDIUM':
        return 'bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500';
      case 'LOW':
        return 'bg-blue-100 dark:bg-blue-900/30 border-l-4 border-blue-500';
      default:
        return 'bg-gray-100 dark:bg-gray-800 border-l-4 border-gray-500';
    }
  }

  /**
   * Get icon for notification type
   */
  getNotificationIcon(type?: string): string {
    switch (type) {
      case 'DEAL_STATUS_CHANGE':
        return '🔄';
      case 'COMMITMENT_RECEIVED':
      case 'COMMITMENT_CONFIRMED':
        return '✅';
      case 'FUNDS_RECEIVED':
        return '💰';
      case 'HOT_SIGNED':
        return '📝';
      case 'INTEREST_DISTRIBUTION':
        return '💵';
      case 'LOAN_EXTENSION':
        return '📅';
      case 'LOAN_DEFAULT':
        return '⚠️';
      case 'CALL_FOR_FUNDS':
        return '📣';
      case 'REDEMPTION_PROCESSED':
        return '✓';
      case 'BORROWER_PAYMENT':
        return '📥';
      case 'DRAWDOWN_EXECUTED':
        return '📤';
      default:
        return '🔔';
    }
  }
}
